import {
  differenceInSeconds,
  format,
  isThisWeek,
  isThisYear,
  isToday,
  isYesterday,
} from "date-fns";
import MerchantDomainBase from "entities/domain/admin/merchants/merchant-domain";
import { IntegrationName } from "../admin/merchants/merchant-integrations";
import ContactDomain from "entities/domain/customers/contact-domain";
import { MessageStatus } from "./message-domain";

export enum ConversationChannel {
  SMS = "sms",
  WHATSAPP = "whatsapp",
  FACEBOOK = "facebook",
  INSTAGRAM = "instagram",
  EMAIL = "email",
  NO_CHANNEL = "no_channel",
}

export interface BlockedByCustomer {
  id: number;
  name: string | null;
}

export enum AllowedMessageType {
  ALL = "all",
  TEMPLATES_ONLY = "templates_only",
}

export enum ConversationSentiment {
  POSITIVE = "positive",
  NEGATIVE = "negative",
  NEUTRAL = "neutral",
  MIXED = "mixed",
}

export enum ConversationUrgency {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
}

export interface ConversationAnalysis {
  sentiment: ConversationSentiment;
  urgency: ConversationUrgency;
  summaryText: string;
}

/**
 * Attachment types the messaging channels accept. Documents render in the
 * thread as `AttachmentType.FILE` download links, so they belong here
 * alongside the media types.
 */
export const MEDIA_AND_DOCUMENT_FILE_FORMATS = [
  "video/mp4",
  "image/*",
  "application/pdf",
  "audio/*",
  "text/plain",
  "text/csv",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
].join(", ");

export default class ConversationDomain {
  id: number;

  version: number | null;

  merchantId: number;

  customerId: number;

  displayName: string;

  customerFirstName: string;

  customerLastName: string;

  customerChannelId: string;

  alias: string;

  channel: ConversationChannel;

  merchantChannelId: string | null;

  whatsappGroupId: string | null;

  isOpen: boolean;

  createdAt: string;

  unreadCount: number;

  assignedAgentId: number | null;

  assignedAiAssistantId?: string;

  inboxId: string | null;

  isSubscribed: boolean;

  isBlocked: boolean;

  previewText: string;

  lastMessageStatus: MessageStatus;

  displayDate: Date;

  messageId: number;

  allowedMessagesType: AllowedMessageType;

  lastMessageTitle?: string;

  lastMessageIsIncoming?: boolean;

  tagIds: string[];

  lastReadAt: Date | null;

  // The newest missed inbound call; already folded into displayDate and
  // unreadCount, kept for callers that need the instant itself.
  lastInboundAttentionAt: Date | null;

  analysis: ConversationAnalysis | null;

  customers: ContactDomain[];

  isContactBlocked: boolean;

  blockedByCustomer: BlockedByCustomer | null;

  get customerCount(): number {
    return this.customers.length;
  }

  // Recency-first relative time for the inbox list. Never a dd/MM/yyyy string —
  // recency is the unit that matters at a glance in an inbox.
  getDisplayDate(): string {
    if (differenceInSeconds(new Date(), this.displayDate) < 60) {
      return "Now";
    } else if (isToday(this.displayDate)) {
      return "Today";
    } else if (isYesterday(this.displayDate)) {
      return "Yesterday";
    } else if (isThisWeek(this.displayDate)) {
      return format(this.displayDate, "eee"); // Short weekday e.g. "Tue"
    } else if (isThisYear(this.displayDate)) {
      return format(this.displayDate, "d MMM"); // e.g. "12 May"
    } else {
      return format(this.displayDate, "d MMM yy"); // e.g. "12 May 24"
    }
  }

  isLastMessageUndelivered(): boolean {
    return this.lastMessageStatus === MessageStatus.UNDELIVERED;
  }

  isGroup(): boolean {
    return this.whatsappGroupId !== null;
  }

  // The conversation's own contact is blocked. blockedByCustomer names only one
  // blocked contact closing the channel, so it cannot answer this when several
  // blocked contacts share the channel.
  isOwnContactBlocked(): boolean {
    for (const customer of this.customers) {
      if (customer.id === this.customerId) {
        return customer.isBlocked;
      }
    }
    return false;
  }

  isAnyUnread(): boolean {
    return this.unreadCount !== 0 && this.unreadCount !== null;
  }

  isTemplatesOnly(): boolean {
    return this.allowedMessagesType === AllowedMessageType.TEMPLATES_ONLY;
  }

  isChannelDisconnected(merchant: MerchantDomainBase): boolean {
    return (
      (this.isChannelFacebookOrInstagram() &&
        !merchant.isIntegrationEnabled(
          this.channel as unknown as IntegrationName
        )) ||
      !merchant.isMerchantChannelEnabled(this.channel)
    );
  }

  getAcceptedFileFormat(): string {
    let fileFormat;

    switch (this.channel) {
      case ConversationChannel.INSTAGRAM:
        fileFormat = "image/*";
        break;
      case ConversationChannel.EMAIL:
        fileFormat = "*";
        break;
      default:
        fileFormat = MEDIA_AND_DOCUMENT_FILE_FORMATS;
        break;
    }

    return fileFormat;
  }

  isEmailChannel(): boolean {
    return this.channel === ConversationChannel.EMAIL;
  }

  isChannelFacebookOrInstagram(): boolean {
    return (
      this.channel === ConversationChannel.FACEBOOK ||
      this.channel === ConversationChannel.INSTAGRAM
    );
  }

  isPhoneBasedChannel(): boolean {
    return (
      this.channel === ConversationChannel.SMS ||
      this.channel === ConversationChannel.WHATSAPP
    );
  }

  constructor(
    id: number,
    merchantId: number,
    customerId: number,
    displayName: string,
    customerChannelId: string,
    alias: string,
    channel: ConversationChannel,
    isOpen: boolean,
    createdAt: string,
    unreadCount: number,
    assignedAgentId: number | null,
    inboxId: string | null,
    isSubscribed: boolean,
    isBlocked: boolean,
    previewText: string,
    lastMessageStatus: MessageStatus,
    displayDate: Date,
    messageId: number,
    allowedMessageType: AllowedMessageType,
    tagIds: string[],
    customerFirstName: string,
    customerLastName: string,
    lastMessageTitle?: string,
    lastMessageIsIncoming?: boolean,
    assignedAiAssistantId?: string,
    lastReadAt?: Date | null,
    customers: ContactDomain[] = [],
    analysis: ConversationAnalysis | null = null,
    merchantChannelId: string | null = null,
    whatsappGroupId: string | null = null,
    lastInboundAttentionAt: Date | null = null,
    version: number | null = null,
    isContactBlocked: boolean = false,
    blockedByCustomer: BlockedByCustomer | null = null
  ) {
    this.id = id;
    this.version = version;
    this.merchantId = merchantId;
    this.customerId = customerId;
    this.displayName = displayName;
    this.customerChannelId = customerChannelId;
    this.alias = alias;
    this.channel = channel;
    this.isOpen = isOpen;
    this.createdAt = createdAt;
    this.unreadCount = unreadCount;
    this.assignedAgentId = assignedAgentId;
    this.inboxId = inboxId;
    this.isSubscribed = isSubscribed;
    this.isBlocked = isBlocked;
    this.previewText = previewText;
    this.customerFirstName = customerFirstName;
    this.customerLastName = customerLastName;
    this.lastMessageStatus = lastMessageStatus;
    this.displayDate = displayDate;
    this.messageId = messageId;
    this.allowedMessagesType = allowedMessageType;
    this.tagIds = tagIds;
    this.lastMessageTitle = lastMessageTitle;
    this.lastMessageIsIncoming = lastMessageIsIncoming;
    this.assignedAiAssistantId = assignedAiAssistantId;
    this.lastReadAt = lastReadAt || null;
    this.customers = customers;
    this.analysis = analysis;
    this.merchantChannelId = merchantChannelId;
    this.whatsappGroupId = whatsappGroupId;
    this.lastInboundAttentionAt = lastInboundAttentionAt;
    this.isContactBlocked = isContactBlocked;
    this.blockedByCustomer = blockedByCustomer;
  }
}
